import { useState } from "react";
import { Link2, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";

/** SUB-1 §2 — inbound emails that look like a submittal return but matched no (or several)
 *  revisions. A person links each to the right revision; it then waits as Pending confirm. */
export default function UnmatchedReturns({ project, items, revisions, onLinked }) {
  const [choice, setChoice] = useState({});
  const [busy, setBusy] = useState("");
  if (!items?.length) return null;

  const link = async (it) => {
    const pkgId = choice[it.id];
    if (!pkgId) { toast.error("Pick the revision first"); return; }
    setBusy(it.id);
    try {
      await opsApi.submittalLinkUnmatched(project.id, it.id, pkgId);
      toast.success("Linked — confirm it on the revision");
      setChoice((c) => ({ ...c, [it.id]: "" }));
      onLinked();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not link that email"); }
    finally { setBusy(""); }
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="ops-unmatched-returns">
      <p className="text-xs font-semibold text-amber-900">Unmatched submittal returns · {items.length}</p>
      <p className="text-[11px] text-amber-800">These came in by email but did not match exactly one revision. Nothing is attached until you link it.</p>
      <ul className="mt-2 divide-y divide-amber-200/70">
        {items.map((it) => (
          <li key={it.id} className="flex flex-wrap items-center gap-2 py-2 text-xs" data-testid={`ops-unmatched-return-${it.id}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-slate-800">{it.subject}</p>
              <p className="text-slate-500">{it.from_name || it.from_email} · {(it.created_at || "").slice(0, 10)}
                {it.submittal_candidates?.length > 1 && ` · ${it.submittal_candidates.length} possible matches`}</p>
            </div>
            {(it.document_ids || []).length > 0 && (
              <button onClick={() => opsApi.openDocument(it.document_ids[it.document_ids.length - 1])}
                      className="inline-flex items-center gap-1 text-sky-700 hover:underline"><FileDown className="h-3 w-3" /> file</button>
            )}
            <select value={choice[it.id] || ""} onChange={(e) => setChoice({ ...choice, [it.id]: e.target.value })}
                    className="h-7 rounded-md border border-slate-200 bg-white px-1.5 text-xs" data-testid={`ops-unmatched-pick-${it.id}`}>
              <option value="">Link to revision…</option>
              {revisions.map((r) => <option key={r.id} value={r.id}>{r.number}{r.revision ? ` Rev ${r.revision}` : ""} — {r.title}</option>)}
            </select>
            <Button size="sm" variant="data" className="h-7 text-xs" disabled={busy === it.id} onClick={() => link(it)} data-testid={`ops-unmatched-link-${it.id}`}>
              <Link2 className="mr-1 h-3 w-3" /> Link
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
